// src/timing/speed-chart-adapter.js

import { calculateMotionProfilePoints } from "./motion-profile.js";

export function createSpeedChartAdapter({ canvasElement, labelElement, chartLib }) {
    let speedChart = null;
    const actualChartLib = chartLib || (typeof window !== "undefined" ? window.Chart : null);

    function init() {
        if (!canvasElement) return;
        const ctx = canvasElement.getContext("2d");
        if (!actualChartLib) {
            console.warn("Chart.js is not loaded.");
            return;
        }
        speedChart = new actualChartLib(ctx, {
            type: "line",
            data: {
                labels: [],
                datasets: [{
                    label: "速度",
                    data: [],
                    borderColor: "rgb(75,192,192)",
                    tension: 0,
                    fill: false,
                    pointRadius: 0
                }]
            },
            options: {
                animation: false,
                scales: {
                    x: { type: "linear", title: { display: true, text: "時間 (s)" } },
                    y: { title: { display: true, text: "速度" }, beginAtZero: true }
                }
            }
        });
    }

    function render({
        selectedAxisId,
        selectedFlowNodeId,
        computed,
        project,
        flowDataSnapshot = {},
        getNodeData,
        getNodeTypeFromData,
        findAxis
    }) {
        if (!speedChart) return;

        const selectedFlowNode = selectedFlowNodeId ? flowDataSnapshot[selectedFlowNodeId] : null;
        const selectedFlowData = selectedFlowNode ? getNodeData(selectedFlowNode) : null;

        if (getNodeTypeFromData(selectedFlowData) === "subprocess" && computed.subprocessTimes[selectedFlowNodeId]) {
            const times = computed.subprocessTimes[selectedFlowNodeId];
            const subprocessName = selectedFlowData.subprocessName || selectedFlowData.label || times.subprocessName || "サブプロセス";
            if (labelElement) {
                labelElement.textContent = `サブプロセス: ${subprocessName} の固定時間プロファイル`;
            }
            speedChart.options.scales.y.title.text = "工程状態";
            speedChart.data.datasets[0].label = "工程状態";
            const level = 1;
            const points = [
                { x: times.startSec, y: 0 },
                { x: times.startSec, y: level },
                { x: times.endSec, y: level },
                { x: times.endSec, y: 0 }
            ];
            speedChart.data.datasets[0].data = points;
            speedChart.data.labels = points.map((p) => p.x);
            speedChart.update();
            return;
        }

        const axisId = selectedAxisId;
        if (!axisId) {
            if (labelElement) {
                labelElement.textContent = "軸を選択すると、その軸の動作を順に並べた速度線図を表示します。";
            }
            speedChart.data.labels = [];
            speedChart.data.datasets[0].data = [];
            speedChart.update();
            return;
        }

        const axis = findAxis(axisId);
        if (!axis) return;

        if (labelElement) {
            labelElement.textContent = `軸: ${axis.name} の速度線図 (時間軸はプロジェクト全体の絶対時刻)`;
        }
        const speedUnit = axis.motionType === "linear" ? project.units.speedLinear : (project.units.speedRotary || "deg/s");
        speedChart.options.scales.y.title.text = "速度 (" + speedUnit + ")";
        speedChart.data.datasets[0].label = "速度 (" + speedUnit + ")";

        const points = [];
        const timeStep = 0.005;
        for (const motion of axis.motions) {
            const times = computed.motionTimes[motion.id];
            if (!times) continue;
            const motionPoints = calculateMotionProfilePoints(motion, axis, times, project.units, timeStep);
            points.push(...motionPoints);
        }

        if (!points.length) {
            points.push({ x: 0, y: 0 });
        }
        speedChart.data.datasets[0].data = points;
        speedChart.data.labels = points.map(p => p.x);
        speedChart.update();
    }

    return {
        init,
        render,
        getRawChart: () => speedChart
    };
}
